import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Copy, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/** Copy a boolean preset to one or more OTHER saved searches.
 *  Renders a checkbox list of other searches; skips the source silently. */
export default function PresetCopyModal({ preset, sourceSearchId, onClose }) {
    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [copying, setCopying] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/recruit/searches?limit=100");
            const others = (data.items || []).filter((s) => s.id !== sourceSearchId);
            setSearches(others);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [sourceSearchId]);
    useEffect(() => { load(); }, [load]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const run = async () => {
        if (selected.size === 0) return;
        setCopying(true);
        try {
            const { data } = await api.post(`/admin/recruit/boolean-presets/${preset.id}/copy`, {
                target_search_ids: Array.from(selected),
            });
            const skipCount = (data.skipped || []).length;
            toast.success(
                `Copied to ${data.copied} search${data.copied === 1 ? "" : "es"}` +
                (skipCount ? ` · ${skipCount} skipped` : "")
            );
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setCopying(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="preset-copy-modal"
        >
            <div
                className="bg-background border border-border rounded-lg max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Copy size={14} className="text-violet-500" />
                        <div>
                            <div className="font-medium text-sm">Copy preset to other searches</div>
                            <div className="text-[11px] text-muted-foreground truncate max-w-[300px]" data-testid="preset-copy-source">
                                <span className="font-medium text-foreground/80">{preset.name}</span> · <span className="font-mono">{preset.query}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded" data-testid="preset-copy-close">
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-violet-500" /></div>
                    ) : searches.length === 0 ? (
                        <div className="p-6 text-center text-sm text-muted-foreground" data-testid="preset-copy-empty">
                            No other searches to copy to. Create another Hiring Brief™ in Job Parser first.
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {searches.map((s) => {
                                const brief = s.brief || {};
                                const title = brief.job_title || "Untitled search";
                                return (
                                    <label
                                        key={s.id}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 cursor-pointer"
                                        data-testid={`preset-copy-target-${s.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected.has(s.id)}
                                            onChange={() => toggle(s.id)}
                                            className="accent-violet-500"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{title}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">
                                                {brief.location || "—"} · {brief.industry || "—"}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-border flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground" data-testid="preset-copy-selection-count">
                        {selected.size} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} data-testid="preset-copy-cancel">Cancel</Button>
                        <Button size="sm" onClick={run} disabled={selected.size === 0 || copying} data-testid="preset-copy-confirm">
                            {copying ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Copy size={12} className="mr-1.5" />}
                            Copy
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
